import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { flashcardDecks, sampleCards } from '../data/mockData';
import SafeIcon from '../common/SafeIcon';
import { FiRefreshCw, FiCheck, FiX, FiChevronRight } from 'react-icons/fi';

const Cards = () => {
  const [activeDeck, setActiveDeck] = useState(null);
  const [currentCardIdx, setCurrentCardIdx] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);

  const startDeck = (deck) => {
    setActiveDeck(deck);
    setCurrentCardIdx(0);
    setIsFlipped(false);
  };

  const nextCard = () => {
    setIsFlipped(false);
    setTimeout(() => {
      setCurrentCardIdx((prev) => (prev + 1) % sampleCards.length);
    }, 150);
  };

  const currentCard = sampleCards[currentCardIdx];

  if (!activeDeck) {
    return (
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold mb-6 flex items-center">
          <SafeIcon icon={FiRefreshCw} className="mr-3 text-[#eaff00]" />
          Memory Matrix Decks
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {flashcardDecks.map(deck => (
            <div key={deck.id} onClick={() => startDeck(deck)} className="glass-panel p-6 rounded-2xl cursor-pointer hover:border-[#eaff00]/50 transition-all group">
              <div className="w-12 h-12 rounded-full bg-[#eaff00]/10 flex items-center justify-center mb-4 group-hover:bg-[#eaff00]/20 transition-colors">
                 <span className="text-[#eaff00] font-bold">{deck.count}</span>
              </div>
              <h3 className="font-semibold text-lg mb-2">{deck.title}</h3>
              <div className="flex justify-between items-center mt-4">
                <span className="text-xs text-slate-500">Last: {deck.lastStudied}</span>
                <SafeIcon icon={FiChevronRight} className="text-[#eaff00] opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto min-h-[70vh] flex flex-col">
      <div className="flex justify-between items-center mb-8">
        <button onClick={() => setActiveDeck(null)} className="text-slate-400 hover:text-white text-sm">
          ← Back to Decks
        </button>
        <span className="text-sm font-medium bg-[#15172b] px-4 py-1 rounded-full border border-[#2a2d4f]">
          Card {currentCardIdx + 1} of {sampleCards.length}
        </span>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center relative perspective-1000">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentCard.id + (isFlipped ? '-back' : '-front')}
            initial={{ rotateY: isFlipped ? -90 : 90, opacity: 0 }}
            animate={{ rotateY: 0, opacity: 1 }}
            exit={{ rotateY: isFlipped ? 90 : -90, opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setIsFlipped(!isFlipped)}
            className="w-full max-w-lg aspect-video glass-panel rounded-3xl p-8 flex items-center justify-center text-center cursor-pointer relative overflow-hidden group"
            style={{ transformStyle: 'preserve-3d' }}
          >
            {/* Subtle glow border on hover */}
            <div className="absolute inset-0 border-2 border-transparent group-hover:border-[#eaff00]/20 rounded-3xl transition-colors"></div>
            
            <p className="text-xl md:text-2xl font-medium leading-relaxed">
              {isFlipped ? currentCard.back : currentCard.front}
            </p>

            <div className="absolute bottom-4 text-xs text-slate-500 flex items-center">
              <SafeIcon icon={FiRefreshCw} className="mr-2" /> Tap to flip
            </div>
          </motion.div>
        </AnimatePresence>

        {isFlipped && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-12 flex space-x-6"
          >
            <button onClick={nextCard} className="w-14 h-14 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center hover:bg-red-500/30 transition-colors border border-red-500/30">
              <SafeIcon icon={FiX} className="text-2xl" />
            </button>
             <button onClick={nextCard} className="w-14 h-14 rounded-full bg-yellow-500/20 text-yellow-400 flex items-center justify-center hover:bg-yellow-500/30 transition-colors border border-yellow-500/30">
              <span className="font-bold">OK</span>
            </button>
            <button onClick={nextCard} className="w-14 h-14 rounded-full bg-[#eaff00]/20 text-[#eaff00] flex items-center justify-center hover:bg-[#eaff00]/30 transition-colors border border-[#eaff00]/30">
              <SafeIcon icon={FiCheck} className="text-2xl" />
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Cards;